import { useForm } from 'react-hook-form'
import { getFaturamentoStatusClasse } from '../utils/faturamentoStatusUtils'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DollarSign, Loader2, Search, Send, CheckCircle2, FileImage, Plus, Save, Download, Edit2 } from 'lucide-react'
import { useToast } from '../../../contexts/NotificationContext'
import { zRequiredDate, zOptionalDate } from '../../../utils/validacao'
import { projetoService } from '../services/projeto'
import { faturamentoService, EtapaFaturamentoDto, FaturamentoDto } from '../services/faturamento'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { FormField, FileUpload } from '../../../componentesCompartilhados/form'
import { useForm as useFormDocumento } from 'react-hook-form'
import { useGlobalLoading } from '../../../contexts/LoadingContext'
import { formatDateToBrazilian } from '../../../utils/formatUtils'
import { calcularValor, somarMinutosApontamentos } from '../../../utils/faturamentoCalculo'
import { format } from 'date-fns'
import { SeletorPeriodo, resolverDatasPeriodo, type TipoPeriodoPreset } from '../../../componentesCompartilhados/periodo'

interface FaturamentoFilters {
  numeroTicket: string
  nomeProjeto: string
  dataInicio: string
  dataFim: string
}

interface FaturamentoTimeEntry {
  idClockifyTimeEntry: number
  idClockify: string
  responsavelNome?: string
  descricao?: string
  dataInicio?: string
  minutos?: number
  horas: number
  tags?: string[]
  cargoCodigo?: number
}

interface FaturamentoProjeto {
  id: number
  idSuporte?: number
  tipo: 'Projeto' | 'Suporte'
  nome: string
  numeroTicket?: string
  dataRecebimento: string
  solicitanteNome?: string
  analistaDDPNome?: string
  analistaSMNNome?: string
  desenvolvedorNome?: string
  tipoProjetoCodigo: number
  valorHora: number
  desenvolvimentoOrcado?: number
  testesOrcado?: number
  descricao?: string
  timeEntries: FaturamentoTimeEntry[]
}

const normalizarProjetoFaturamento = (projeto: any): FaturamentoProjeto => {
  const tipo = (projeto.tipo ?? projeto.Tipo ?? 'Projeto') as 'Projeto' | 'Suporte'
  const idProjeto = Number(projeto.id ?? projeto.Id ?? projeto.idProjeto ?? projeto.IdProjeto ?? 0)
  const idSuporte = Number(projeto.idSuporte ?? projeto.IdSuporte ?? 0)
  const id = tipo === 'Suporte' ? idSuporte : idProjeto

  return {
    id,
    idSuporte: tipo === 'Suporte' ? idSuporte : undefined,
    tipo,
    nome: projeto.nome ?? projeto.Nome ?? '',
    numeroTicket: projeto.numeroTicket ?? projeto.NumeroTicket,
    dataRecebimento: projeto.dataRecebimento ?? projeto.DataRecebimento ?? '',
    solicitanteNome: projeto.solicitanteNome ?? projeto.SolicitanteNome,
    analistaDDPNome: projeto.analistaDDPNome ?? projeto.AnalistaDDPNome,
    analistaSMNNome: projeto.analistaSMNNome ?? projeto.AnalistaSMNNome,
    desenvolvedorNome: projeto.desenvolvedorNome ?? projeto.DesenvolvedorNome,
    tipoProjetoCodigo: Number(projeto.tipoProjetoCodigo ?? projeto.TipoProjetoCodigo ?? 0),
    valorHora: Number(projeto.valorHora ?? projeto.ValorHora ?? 0),
    desenvolvimentoOrcado: projeto.desenvolvimentoOrcado ?? projeto.DesenvolvimentoOrcado,
    testesOrcado: projeto.testesOrcado ?? projeto.TestesOrcado,
    descricao: projeto.descricao ?? projeto.Descricao ?? '',
    timeEntries: projeto.timeEntries ?? projeto.TimeEntries ?? []
  }
}

/** Percentual sobre orçamento (tipo projeto Orçamento) — não misturar com etapas gerais. */
const ETAPAS_PROPORCIONAIS_ORCAMENTO: number[] = [3, 4, 5]

/** Etapas cadastradas no banco (7–9): disponíveis no envio para qualquer tipo de projeto ou suporte. */
const ETAPAS_FATURAMENTO_GLOBAL: number[] = [7, 8, 9]

function mesclarEtapasFaturamentoGlobais(disponiveis: EtapaFaturamentoDto[], todas: EtapaFaturamentoDto[]): EtapaFaturamentoDto[] {
  const codigos = new Set(disponiveis.map(e => e.codigoEtapaFaturamento))
  const extras = todas.filter(e => ETAPAS_FATURAMENTO_GLOBAL.includes(e.codigoEtapaFaturamento) && !codigos.has(e.codigoEtapaFaturamento))
  if (extras.length === 0)
    return disponiveis

  return [...disponiveis, ...extras].sort((a, b) => a.codigoEtapaFaturamento - b.codigoEtapaFaturamento)
}

const faturamentoSchema = z.object({
  numeroTicket: z.string().optional(),
  nomeProjeto: z.string().optional(),
  dataInicio: zRequiredDate,
  dataFim: zOptionalDate,
}).superRefine((data, ctx) => {
  if (!data.numeroTicket?.trim() && !data.nomeProjeto?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['numeroTicket'],
      message: 'Preencha pelo menos o Ticket ou o Nome do Projeto',
    })
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['nomeProjeto'],
      message: 'Preencha pelo menos o Ticket ou o Nome do Projeto',
    })
  }
  
  // Validar que data fim seja maior que data início
  if (data.dataFim && data.dataInicio) {
    const dataInicio = new Date(data.dataInicio)
    const dataFim = new Date(data.dataFim)
    
    if (dataFim < dataInicio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataFim'],
        message: 'Data fim deve ser maior ou igual à data início',
      })
    }
  }
})

export default function FaturamentoAnalista() {
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [projetos, setProjetos] = useState<FaturamentoProjeto[]>([])
  const [projetosTodos, setProjetosTodos] = useState<FaturamentoProjeto[]>([])
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<FaturamentoFilters | null>(null)
  const [showModalProjetos, setShowModalProjetos] = useState(false)
  const [observacao, setObservacao] = useState('')
  const [documentosPorProjeto, setDocumentosPorProjeto] = useState<Record<number, any[]>>({})
  const [showFormDocumento, setShowFormDocumento] = useState<Record<number, boolean>>({})
  const [documentoEditando, setDocumentoEditando] = useState<Record<number, any>>({})
  const [etapasFaturamento, setEtapasFaturamento] = useState<EtapaFaturamentoDto[]>([])
  const [etapaSelecionadaPorProjeto, setEtapaSelecionadaPorProjeto] = useState<Record<number, number>>({})
  const [faturamentosEnviadosPorProjeto, setFaturamentosEnviadosPorProjeto] = useState<Record<number, FaturamentoDto[]>>({})
  const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodoPreset>('ultimos30')
  const [dataReferenciaPeriodo, setDataReferenciaPeriodo] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [intervaloInicio, setIntervaloInicio] = useState('')
  const [intervaloFim, setIntervaloFim] = useState('')
  const [pulsoBuscaAposAplicarPeriodo, setPulsoBuscaAposAplicarPeriodo] = useState(0)

  const { startLoading, stopLoading } = useGlobalLoading()
  
  const { register: registerDocumento, handleSubmit: handleSubmitDocumento, formState: { errors: errorsDocumento }, reset: resetDocumento, setValue: setValueDocumento, watch: watchDocumento } = useFormDocumento<{
    nomeArquivo: string
    tipoDocumentoCodigo: string
    arquivo: File | null
    projetoId: number
  }>({
    defaultValues: {
      nomeArquivo: '',
      tipoDocumentoCodigo: '',
      arquivo: null,
      projetoId: 0
    }
  })
  
  const selectedFileDocumento = watchDocumento('arquivo')

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FaturamentoFilters>({
    resolver: zodResolver(faturamentoSchema),
    mode: 'onSubmit',
    defaultValues: {
      numeroTicket: '',
      nomeProjeto: '',
      dataInicio: '',
      dataFim: '',
    },
  })

  useEffect(() => {
    const { dataInicio: di, dataFim: df } = resolverDatasPeriodo(tipoPeriodo, dataReferenciaPeriodo, intervaloInicio, intervaloFim)
    setValue('dataInicio', di)
    setValue('dataFim', df)
  }, [tipoPeriodo, dataReferenciaPeriodo, intervaloInicio, intervaloFim, setValue])

  // Ler parâmetro da URL e fazer busca automática
  useEffect(() => {
    const numeroTicket = searchParams.get('numeroTicket')
    if (numeroTicket) {
      setValue('numeroTicket', numeroTicket)
      const dataFim = format(new Date(), 'yyyy-MM-dd')
      const dataInicioDt = new Date()
      dataInicioDt.setMonth(dataInicioDt.getMonth() - 1)
      const dataInicioStr = format(dataInicioDt, 'yyyy-MM-dd')
      setTipoPeriodo('intervalo')
      setIntervaloInicio(dataInicioStr)
      setIntervaloFim(dataFim)
      setTimeout(() => {
        handleSubmit(onSubmit)()
      }, 100)
      
      // Limpar parâmetro da URL após ler
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('numeroTicket')
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, setValue, handleSubmit, setSearchParams])

  // Buscar faturamentos enviados quando projetos são carregados
  useEffect(() => {
    const buscarFaturamentos = async () => {
      if (projetos.length > 0) {
        try {
          startLoading('Carregando faturamentos...')
          for (const projeto of projetos) {
            try {
              const faturamentos = await faturamentoService.obterFaturamentosPorProjeto(projeto.id)
              
              // IMPORTANTE: Exibir TODOS os faturamentos do projeto, independente de terem dataEnvio ou não
              // O status será determinado pela dataAprovacao (se tem = Faturado, se não tem = Pendente)
              // A data de exibição será dataEnvio se existir, senão será dataCadastro
              setFaturamentosEnviadosPorProjeto(prev => ({ ...prev, [projeto.id]: faturamentos }))
              
              // Buscar documentos do projeto (Aprovação e Histórico do Ticket)
              try {
                const documentosProjeto = await projetoService.buscarDocumentos(projeto.id)
                const documentosFaturamento = documentosProjeto.filter((d: any) => d.tipoDocumentoCodigo === 4 || d.tipoDocumentoCodigo === 5)
                setDocumentosPorProjeto(prev => ({ ...prev, [projeto.id]: documentosFaturamento }))
              } catch (error) {
              }
              
            } catch (error) {
              // Ignorar erros ao buscar faturamentos
            }
          }
          if (projetos.length === 1) {
            setObservacao(projetos[0].descricao || '')
          } else {
            setObservacao('')
          }
        } finally {
          stopLoading()
        }
      } else {
        setObservacao('')
        setFaturamentosEnviadosPorProjeto({})
      }
    }
    buscarFaturamentos()
  }, [projetos, startLoading, stopLoading])

  useEffect(() => {
    const carregarEtapas = async () => {
      try {
        startLoading('Carregando etapas...')
        const etapas = await faturamentoService.obterEtapasFaturamento()
        setEtapasFaturamento(etapas)
      } catch (error: any) {
      } finally {
        stopLoading()
      }
    }
    carregarEtapas()
  }, [startLoading, stopLoading])

  const onSubmit = useCallback(async (data: FaturamentoFilters) => {
    if (tipoPeriodo === 'intervalo')
    {
      const ai = intervaloInicio.trim()
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ai))
      {
        toast.error('Período', 'Informe a data inicial no período personalizado')
        return
      }
      const afRaw = intervaloFim.trim()
      const af = afRaw && /^\d{4}-\d{2}-\d{2}$/.test(afRaw) ? afRaw : format(new Date(), 'yyyy-MM-dd')
      if (af < ai)
      {
        toast.error('Período', 'A data final deve ser igual ou posterior à inicial')
        return
      }
    }
    setIsLoading(true)
    setProjetos([])
    setSelectedEntries(new Set())
    setFormData(data)
    
    const buscaPorNome = !data.numeroTicket?.trim() && !!data.nomeProjeto?.trim()
    
    try {
      startLoading('Buscando projetos...')
      const payload: any = {
        numeroTicket: data.numeroTicket?.trim() || null,
        nomeProjeto: data.nomeProjeto?.trim() || null,
        dataInicio: data.dataInicio,
      }
      
      if (data.dataFim?.trim()) {
        payload.dataFim = data.dataFim
      }
      
      const response = await projetoService.buscarProjetosFaturamento(payload)
      
      const projetosEncontrados = (response || []).map(normalizarProjetoFaturamento)
      setProjetosTodos(projetosEncontrados)
      
      if (buscaPorNome && projetosEncontrados.length > 1) {
        setShowModalProjetos(true)
        setProjetos([])
      } else {
        setProjetos(projetosEncontrados)
      }
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao buscar projetos')
    } finally {
      setIsLoading(false)
      stopLoading()
    }
  }, [tipoPeriodo, intervaloInicio, intervaloFim, toast, startLoading, stopLoading])

  const onInvalid = useCallback((validationErrors: any) => {
    if (validationErrors.numeroTicket || validationErrors.nomeProjeto) {
      const errorMessage = validationErrors.numeroTicket?.message || validationErrors.nomeProjeto?.message
      if (errorMessage) {
        toast.error('Validação', errorMessage)
      }
    }
  }, [toast])

  const handleSubmitRef = useRef(handleSubmit)
  handleSubmitRef.current = handleSubmit
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit
  const onInvalidRef = useRef(onInvalid)
  onInvalidRef.current = onInvalid

  useEffect(() => {
    if (pulsoBuscaAposAplicarPeriodo === 0)
      return
    void handleSubmitRef.current(onSubmitRef.current, onInvalidRef.current)()
  }, [pulsoBuscaAposAplicarPeriodo])

  const handleSelecionarProjeto = (projetoSelecionado: FaturamentoProjeto) => {
    setProjetos([projetoSelecionado])
    setShowModalProjetos(false)
  }

  const toggleEntrySelection = (id: number) => {
    const newSelected = new Set(selectedEntries)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedEntries(newSelected)
  }
  
  const handleAbrirFormDocumento = (projetoId: number, tipo: 4 | 5) => {
    setShowFormDocumento(prev => ({ ...prev, [projetoId]: true }))
    setDocumentoEditando(prev => ({ ...prev, [projetoId]: null }))
    resetDocumento({
      nomeArquivo: '',
      tipoDocumentoCodigo: tipo.toString(),
      arquivo: null,
      projetoId
    })
  }
  
  const handleEditarDocumento = (projetoId: number, doc: any) => {
    setDocumentoEditando(prev => ({ ...prev, [projetoId]: doc }))
    setShowFormDocumento(prev => ({ ...prev, [projetoId]: true }))
    resetDocumento({
      nomeArquivo: doc.nomeArquivo || doc.tipoDocumentoNome || '',
      tipoDocumentoCodigo: doc.tipoDocumentoCodigo.toString(),
      arquivo: null,
      projetoId
    })
  }
  
  const handleCancelarDocumento = (projetoId: number) => {
    setShowFormDocumento(prev => ({ ...prev, [projetoId]: false }))
    setDocumentoEditando(prev => {
      const newState = { ...prev }
      delete newState[projetoId]
      return newState
    })
    resetDocumento()
  }
  
  const handleSalvarDocumento = async (data: { nomeArquivo: string; tipoDocumentoCodigo: string; arquivo: File | null; projetoId: number }) => {
    if (!data.arquivo && !documentoEditando[data.projetoId]) {
      toast.error('Erro', 'Arquivo é obrigatório')
      return
    }
    
    try {
      startLoading('Enviando documento...')
      
      const docEditando = documentoEditando[data.projetoId]
      
      if (docEditando) {
        await projetoService.atualizarDocumento(
          docEditando.idDocumento,
          data.projetoId,
          Number(data.tipoDocumentoCodigo),
          data.arquivo || undefined,
          data.nomeArquivo
        )
        toast.success('Sucesso', 'Documento atualizado com sucesso!')
      } else {
        if (!data.arquivo) {
          toast.error('Erro', 'Arquivo é obrigatório')
          return
        }
        await faturamentoService.uploadDocumento(
          data.projetoId,
          Number(data.tipoDocumentoCodigo),
          data.arquivo,
          data.nomeArquivo
        )
        toast.success('Sucesso', 'Documento enviado com sucesso!')
      }
      
      // Recarregar documentos
      const documentosProjeto = await projetoService.buscarDocumentos(data.projetoId)
      const documentosFaturamento = documentosProjeto.filter((d: any) => d.tipoDocumentoCodigo === 4 || d.tipoDocumentoCodigo === 5)
      setDocumentosPorProjeto(prev => ({ ...prev, [data.projetoId]: documentosFaturamento }))
      
      handleCancelarDocumento(data.projetoId)
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao salvar documento')
    } finally {
      stopLoading()
    }
  }
  
  const handleDownloadDocumento = async (_projetoId: number, idDocumento: number, nomeArquivo: string) => {
    try {
      startLoading('Baixando documento...')
      const blob = await faturamentoService.downloadDocumento(idDocumento)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nomeArquivo
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Sucesso', 'Documento baixado com sucesso!')
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao baixar documento')
    } finally {
      stopLoading()
    }
  }

  const handleEnviar = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (projetos.length === 0) {
      toast.error('Erro', 'Nenhum projeto selecionado')
      return
    }

    if (projetos.length > 1) {
      toast.error('Erro', 'Selecione apenas um projeto para enviar')
      return
    }

    const projeto = projetos[0]
    const idsSelecionados = Array.from(selectedEntries)
    const codigoEtapaFaturamento = etapaSelecionadaPorProjeto[projeto.id]

    if (!codigoEtapaFaturamento) {
      toast.error('Erro', 'Selecione uma etapa de faturamento')
      return
    }

    const usarCalculoBaseadoOrcamento = projeto.tipoProjetoCodigo === 3
      && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(codigoEtapaFaturamento)

    if (!usarCalculoBaseadoOrcamento && idsSelecionados.length === 0) {
      toast.error('Erro', 'Selecione ao menos um apontamento para incluir no faturamento')
      return
    }

    setIsSubmitting(true)
    try {
      startLoading('Enviando faturamento...')
      await projetoService.inserirApontamento(projeto.id, idsSelecionados, codigoEtapaFaturamento, observacao, projeto.tipo === 'Suporte')
      toast.success('Sucesso', 'Faturamento gerado com sucesso!')
      
      setSelectedEntries(new Set())
      setObservacao('')
      setEtapaSelecionadaPorProjeto(prev => {
        const newState = { ...prev }
        delete newState[projeto.id]
        return newState
      })

      // Atualizar a Base de Custo com o novo faturamento (uma linha por envio)
      const faturamentosAtualizados = await faturamentoService.obterFaturamentosPorProjeto(projeto.id)
      setFaturamentosEnviadosPorProjeto(prev => ({ ...prev, [projeto.id]: faturamentosAtualizados }))
      
      // Recarregar os dados do servidor após envio bem-sucedido
      if (formData) {
        setIsLoading(true)
        const payload: any = {
          numeroTicket: formData.numeroTicket?.trim() || null,
          nomeProjeto: formData.nomeProjeto?.trim() || null,
          dataInicio: formData.dataInicio,
        }
        
        if (formData.dataFim?.trim()) {
          payload.dataFim = formData.dataFim
        }
        
        const response = await projetoService.buscarProjetosFaturamento(payload)
        const projetosEncontrados = response || []
        
        if (projetosEncontrados.length === 0) {
          setProjetos([])
        } else if (projetosEncontrados.length === 1) {
          setProjetos(projetosEncontrados)
        } else {
          setProjetosTodos(projetosEncontrados)
          setShowModalProjetos(true)
          setProjetos([])
        }
      }
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao gerar faturamento')
    } finally {
      setIsSubmitting(false)
      setIsLoading(false)
      stopLoading()
    }
  }

  const formatarHoras = (minutos: number): string => {
    const totalSegundos = Math.floor(minutos * 60)
    const horas = Math.floor(totalSegundos / 3600)
    const mins = Math.floor((totalSegundos % 3600) / 60)
    const segs = totalSegundos % 60
    
    return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(segs).padStart(2, '0')}`
  }

  const calcularResumoPorUsuario = (timeEntries: FaturamentoTimeEntry[], projeto: FaturamentoProjeto, etapaSelecionada: number | undefined) => {
    if (etapaSelecionada !== undefined && etapaSelecionada !== null && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)) {
      const desenvolvimentoOrcado = projeto.desenvolvimentoOrcado || 0
      const testesOrcado = projeto.testesOrcado || 0
      const totalOrcado = desenvolvimentoOrcado + testesOrcado

      const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === etapaSelecionada)
      const percentualEtapa = etapa ? etapa.percentual / 100 : 1

      const minutosDesenvolvimento = Math.round(desenvolvimentoOrcado * percentualEtapa * 60)
      const minutosTestes = Math.round(testesOrcado * percentualEtapa * 60)

      if (totalOrcado === 0) {
        return []
      }

      const resumo: Array<{ usuario: string; minutos: number; horasFormatada: string }> = []
      const nomeDesenvolvedor = projeto.desenvolvedorNome || '-'
      const nomeAnalista = projeto.analistaSMNNome || '-'

      resumo.push({
        usuario: nomeDesenvolvedor,
        minutos: minutosDesenvolvimento,
        horasFormatada: formatarHoras(minutosDesenvolvimento)
      })

      resumo.push({
        usuario: nomeAnalista,
        minutos: minutosTestes,
        horasFormatada: formatarHoras(minutosTestes)
      })

      return resumo
    }

    // Cálculo normal baseado nas horas reais (time entries)
    const resumo = new Map<string, { minutos: number }>()
    
    timeEntries.forEach(entry => {
      const usuario = entry.responsavelNome || 'Desconhecido'
      const minutos = entry.minutos || 0
      
      if (!resumo.has(usuario)) {
        resumo.set(usuario, { minutos: 0 })
      }
      
      const atual = resumo.get(usuario)!
      resumo.set(usuario, {
        minutos: atual.minutos + minutos
      })
    })
    
    return Array.from(resumo.entries()).map(([usuario, valores]) => ({
      usuario,
      minutos: valores.minutos,
      horasFormatada: formatarHoras(valores.minutos)
    }))
  }

  // @ts-expect-error - Função mantida para uso futuro
  const _calcularTotalGeral = (projetos: FaturamentoProjeto[]) => {
    let totalMinutos = 0
    
    projetos.forEach(projeto => {
      projeto.timeEntries.forEach(entry => {
        totalMinutos += entry.minutos || 0
      })
    })
    
    return {
      minutos: totalMinutos,
      horasFormatada: formatarHoras(totalMinutos)
    }
  }

  // const totalGeral = calcularTotalGeral(projetos) // Não utilizado no momento

  return (
    <div className="space-y-6 relative">
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
            <p className="text-gray-700 font-medium">Processando faturamento...</p>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faturamento</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerencie o faturamento dos projetos
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
            <p className="text-sm text-gray-600">Busque projetos para faturamento</p>
          </div>
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit(onSubmit, onInvalid)(e)
          }} 
          className="space-y-4"
          noValidate
        >
          <input type="hidden" {...register('dataInicio')} />
          <input type="hidden" {...register('dataFim')} />
          <div className="max-w-2xl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket
                </label>
                <input
                  type="text"
                  {...register('numeroTicket', {
                    onChange: (e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                      e.target.value = numericValue
                    }
                  })}
                  maxLength={6}
                  placeholder="Ex: 12345"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Projeto
                </label>
                <input
                  type="text"
                  {...register('nomeProjeto')}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  placeholder="Digite o nome do projeto"
                />
              </div>
            </div>
          </div>

          <SeletorPeriodo
            tipoPeriodo={tipoPeriodo}
            onTipoPeriodoChange={setTipoPeriodo}
            dataReferencia={dataReferenciaPeriodo}
            onDataReferenciaChange={setDataReferenciaPeriodo}
            intervaloInicio={intervaloInicio}
            intervaloFim={intervaloFim}
            onIntervaloInicioChange={setIntervaloInicio}
            onIntervaloFimChange={setIntervaloFim}
            descricaoAjuda="(período considerado na busca de projetos)"
            onAplicarConfirmado={() =>
              setPulsoBuscaAposAplicarPeriodo((p) => p + 1)
            }
          />
          {(errors.dataInicio?.message ?? errors.dataFim?.message) && (
            <p className="text-sm text-red-600">{errors.dataInicio?.message || errors.dataFim?.message}</p>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2 inline" />
                  Buscar
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {projetos.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Resultados do Faturamento</h3>
          </div>

          {projetos.map((projeto) => {
            // Verificar se há apontamentos disponíveis para enviar
            const timeEntriesDisponiveis = projeto.timeEntries
            const temApontamentosParaEnviar = timeEntriesDisponiveis.length > 0
            
            // Obter faturamentos do projeto para aplicar regras de filtro
            const faturamentosProjeto = faturamentosEnviadosPorProjeto[projeto.id] || []
            const etapasJaFaturadas = faturamentosProjeto.map(f => f.codigoEtapaFaturamento)

            const idsTimeEntries = projeto.timeEntries.map(entry => entry.idClockifyTimeEntry)
            const todosSelecionados = idsTimeEntries.length > 0 && idsTimeEntries.every(id => selectedEntries.has(id))
            const toggleSelecionarTodos = () => {
              const novosSelecionados = new Set(selectedEntries)
              if (todosSelecionados) {
                idsTimeEntries.forEach(id => {
                  novosSelecionados.delete(id)
                })
              } else {
                idsTimeEntries.forEach(id => {
                  novosSelecionados.add(id)
                })
              }
              setSelectedEntries(novosSelecionados)
            }

            const etapaSelecionada = etapaSelecionadaPorProjeto[projeto.id]
            const ocultarApontamentos = projeto.tipoProjetoCodigo === 3
              && etapaSelecionada !== undefined
              && etapaSelecionada !== null
              && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)
            
            let etapasDisponiveis: EtapaFaturamentoDto[]

            const ehSuporte = projeto.tipo === 'Suporte'

            // Filtrar etapas baseado no tipo de projeto:
            // - Suporte: sempre permitir Apontamento (2) e Homologação (6), independente de envios já realizados
            // - Tipo Orçamento (3): regras especiais por etapa/percentual
            // - Demais tipos: regras normais de projeto por apontamento
            // Etapas 7–9 (EtapaFaturamento no banco) são sempre acrescentadas após este bloco para todos os tipos.
            if (ehSuporte) {
              etapasDisponiveis = etapasFaturamento.filter(
                (e) => e.codigoEtapaFaturamento === 2 || e.codigoEtapaFaturamento === 6,
              )
            } else if (projeto.tipoProjetoCodigo === 3) {
              etapasDisponiveis = etapasFaturamento
              if (!temApontamentosParaEnviar) {
                etapasDisponiveis = etapasFaturamento.filter(e =>
                  ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(e.codigoEtapaFaturamento),
                )
              }
            }
            else {
              etapasDisponiveis = etapasFaturamento.filter(e => e.codigoEtapaFaturamento === 1 || e.codigoEtapaFaturamento === 2 || e.codigoEtapaFaturamento === 6)
            }

            etapasDisponiveis = mesclarEtapasFaturamentoGlobais(etapasDisponiveis, etapasFaturamento)
            
            // Aplicar regras de filtro baseado nos faturamentos existentes (apenas para projetos, não para suporte)
            if (!ehSuporte) {
              // Regra 1: Se houver faturamento com etapa 2 (Apontamento) ou 3 (Início Desenvolvimento), ocultar essas opções
              if (etapasJaFaturadas.includes(2)) {
                etapasDisponiveis = etapasDisponiveis.filter(e => e.codigoEtapaFaturamento !== 2)
              }
              if (etapasJaFaturadas.includes(3)) {
                etapasDisponiveis = etapasDisponiveis.filter(e => e.codigoEtapaFaturamento !== 3)
              }
              
              // Regra 1.5: Se houver faturamento com etapa 2 (Apontamento) ou 3 (Início Desenvolvimento), ocultar a etapa 1 (Escopo e Orçamento)
              if (etapasJaFaturadas.includes(2) || etapasJaFaturadas.includes(3)) {
                etapasDisponiveis = etapasDisponiveis.filter(e => e.codigoEtapaFaturamento !== 1)
              }
              
              // Regra 2: etapas 3, 4 ou 5 (proporcionais) — ocultar código já faturado
              const proporcionaisJaFaturadas = etapasJaFaturadas.filter((e): e is number =>
                e !== undefined && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(e))
              etapasDisponiveis = etapasDisponiveis.filter(e => !proporcionaisJaFaturadas.includes(e.codigoEtapaFaturamento))
            }

            // Etapas 7–9: ocultar após já faturadas (vale para projeto e suporte)
            const etapasGlobaisJaFaturadas = etapasJaFaturadas.filter((e): e is number =>
              e !== undefined && ETAPAS_FATURAMENTO_GLOBAL.includes(e))
            etapasDisponiveis = etapasDisponiveis.filter(e => !etapasGlobaisJaFaturadas.includes(e.codigoEtapaFaturamento))

            // Regra final: etapas 2 (Apontamento) e 6 (Homologação) devem sempre constar
            // quando houver apontamentos pendentes de envio, independente do tipo de projeto
            if (temApontamentosParaEnviar) {
              const codigosGarantidos = [2, 6]
              const codigosAtuais = new Set(etapasDisponiveis.map(e => e.codigoEtapaFaturamento))
              const etapasParaReincluir = etapasFaturamento.filter(
                e => codigosGarantidos.includes(e.codigoEtapaFaturamento) && !codigosAtuais.has(e.codigoEtapaFaturamento)
              )
              if (etapasParaReincluir.length > 0) {
                etapasDisponiveis = [...etapasDisponiveis, ...etapasParaReincluir]
                  .sort((a, b) => a.codigoEtapaFaturamento - b.codigoEtapaFaturamento)
              }
            }

            return (
              <div key={projeto.id} className="mb-6 pb-6 border-b border-gray-200 last:border-0">
              {etapasDisponiveis.length > 0 && (
                <div className="mb-4 max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Etapa de Faturamento <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={etapaSelecionadaPorProjeto[projeto.id] || ''}
                    onChange={(e) => setEtapaSelecionadaPorProjeto(prev => ({ ...prev, [projeto.id]: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    <option value="">Selecione uma etapa</option>
                    {etapasDisponiveis.map((etapa) => (
                      <option key={etapa.codigoEtapaFaturamento} value={etapa.codigoEtapaFaturamento}>
                        {etapa.descricao} ({etapa.percentual}%)
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,480px)_1fr] gap-6 mb-4 items-start">
                {/* Informações do Projeto - Esquerda */}
                <div className="min-w-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Nome do Projeto</p>
                      <p className="font-semibold text-gray-900">{projeto.nome}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Data de Recebimento</p>
                      <p className="font-semibold text-gray-900">
                        {formatDateToBrazilian(projeto.dataRecebimento) || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Solicitante</p>
                      <p className="font-semibold text-gray-900">{projeto.solicitanteNome || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Analista DDP</p>
                      <p className="font-semibold text-gray-900">{projeto.analistaDDPNome || '-'}</p>
                    </div>
                    {projeto.analistaSMNNome && (
                      <div>
                        <p className="text-sm text-gray-600">Analista SMN</p>
                        <p className="font-semibold text-gray-900">{projeto.analistaSMNNome}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Base de Custo - Direita - Começando no topo */}
                {(() => {
                  const formatarMoeda = (valor: number) => {
                    return new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(valor)
                  }

                  const formatarHorasDecimal = (minutos: number): string => {
                    const horas = minutos / 60
                    return horas.toFixed(2).replace('.', ',')
                  }

                  const formatarHorasHorario = (minutos: number): string => {
                    const totalSegundos = Math.floor(minutos * 60)
                    const horas = Math.floor(totalSegundos / 3600)
                    const mins = Math.floor((totalSegundos % 3600) / 60)
                    const segs = totalSegundos % 60
                    
                    return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(segs).padStart(2, '0')}`
                  }

                  const valorHora = projeto.valorHora
                  const etapaSelecionada = etapaSelecionadaPorProjeto[projeto.id]
                  
                  // Calcular base de custo do faturamento atual baseado na etapa
                  const calcularBaseCustoAtual = () => {
                    const timeEntriesSelecionados = projeto.timeEntries.filter(entry => selectedEntries.has(entry.idClockifyTimeEntry))
                    
                    // Verificar se é projeto tipo orçamento (3) e etapa especial (3, 4, 5)
                    const usarCalculoBaseadoOrcamento = projeto.tipoProjetoCodigo === 3 
                      && etapaSelecionada !== undefined 
                      && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)
                      && projeto.desenvolvimentoOrcado !== undefined
                      && projeto.testesOrcado !== undefined

                    if (usarCalculoBaseadoOrcamento) {
                      // Calcular baseado no orçamento
                      const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === etapaSelecionada)
                      if (!etapa) return { minutos: 0, valor: 0 }

                      const desenvolvimentoOrcado = projeto.desenvolvimentoOrcado || 0
                      const testesOrcado = projeto.testesOrcado || 0
                      const totalHorasOrcado = desenvolvimentoOrcado + testesOrcado
                      
                      // Aplicar percentual da etapa
                      const percentual = etapa.percentual / 100
                      const horasAposPercentual = totalHorasOrcado * percentual
                      const minutosAposPercentual = horasAposPercentual * 60
                      
                      const valorTotal = Math.round(horasAposPercentual * valorHora * 100) / 100
                      
                      return { minutos: minutosAposPercentual, valor: valorTotal }
                    } else {
                      const totalMinutos = somarMinutosApontamentos(timeEntriesSelecionados)
                      const valorTotal = calcularValor(totalMinutos, valorHora)
                      
                      return { minutos: totalMinutos, valor: valorTotal }
                    }
                  }

                  const baseCustoAtual = calcularBaseCustoAtual()
                  const dataHoje = new Date().toLocaleDateString('pt-BR')
                  
                  // Obter TODOS os faturamentos do projeto (ordenar por data de cadastro, mais recente primeiro)
                  // IMPORTANTE: Não filtrar por dataEnvio - exibir todos os faturamentos
                  const faturamentosEnviados = (faturamentosEnviadosPorProjeto[projeto.id] || [])
                    .sort((a, b) => {
                      // Ordenar por dataEnvioAnalista se existir, senão por dataCadastro
                      const dataA = a.dataEnvioAnalista 
                        ? new Date(a.dataEnvioAnalista).getTime() 
                        : (a.dataCadastro ? new Date(a.dataCadastro).getTime() : 0)
                      const dataB = b.dataEnvioAnalista 
                        ? new Date(b.dataEnvioAnalista).getTime() 
                        : (b.dataCadastro ? new Date(b.dataCadastro).getTime() : 0)
                      return dataB - dataA // Mais recente primeiro
                    })
                  
                  // Uma linha na Base de Custo por envio (cada faturamento = um envio com total dos apontamentos daquele envio).
                  const faturamentosEnviadosLinhas: Array<{
                    dataEnvioAnalista: string
                    dataEnvioMomentum: string
                    dataFaturamento: string
                    minutos: number
                    valor: number
                    observacao: string
                    statusCodigo?: number
                    codigoEtapaFaturamento?: number
                  }> = []
                  faturamentosEnviados.forEach(fat => {
                    // Verificar se é projeto tipo orçamento (3) e etapa especial (3, 4, 5)
                    const codigoEtapa = fat.codigoEtapaFaturamento
                    const usarCalculoBaseadoOrcamento = projeto.tipoProjetoCodigo === 3 
                      && codigoEtapa !== undefined 
                      && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(codigoEtapa)
                      && projeto.desenvolvimentoOrcado !== undefined
                      && projeto.testesOrcado !== undefined

                    let minutosData = 0
                    let valorData = 0

                    if (usarCalculoBaseadoOrcamento && fat.valorTotal > 0) {
                      valorData = fat.valorTotal
                      const horasDecimal = valorData / valorHora
                      minutosData = horasDecimal * 60
                    } else if (fat.apontamentos && fat.apontamentos.length > 0) {
                      minutosData = somarMinutosApontamentos(fat.apontamentos)
                      valorData = calcularValor(minutosData, valorHora)
                    } else if (fat.valorTotal > 0) {
                      valorData = fat.valorTotal
                      const horasDecimal = valorData / valorHora
                      minutosData = horasDecimal * 60
                    }

                    // Exibir TODOS os faturamentos, mesmo que não tenham minutos/valor calculado
                    // Status baseado na tabela de status do faturamento
                    const dataEnvioAnalistaFormatada = fat.dataEnvioAnalista
                      ? (formatDateToBrazilian(fat.dataEnvioAnalista) || '-')
                      : '-'
                    const dataEnvioMomentumFormatada = fat.dataEnvioMomentum
                      ? (formatDateToBrazilian(fat.dataEnvioMomentum) || '-')
                      : '-'
                    const dataFaturamentoFormatada = fat.dataFaturamento
                      ? (formatDateToBrazilian(fat.dataFaturamento) || '-')
                      : '-'
                    const statusDescricao = fat.statusDescricao || 'Pendente'
                    
                    faturamentosEnviadosLinhas.push({
                      dataEnvioAnalista: dataEnvioAnalistaFormatada,
                      dataEnvioMomentum: dataEnvioMomentumFormatada,
                      dataFaturamento: dataFaturamentoFormatada,
                      minutos: minutosData,
                      valor: valorData,
                      observacao: statusDescricao,
                      statusCodigo: fat.statusCodigo,
                      codigoEtapaFaturamento: fat.codigoEtapaFaturamento
                    })
                  })

                  // Calcular totais
                  let totalMinutos = baseCustoAtual.minutos
                  let totalValor = baseCustoAtual.valor
                  
                  faturamentosEnviadosLinhas.forEach(fat => {
                    totalMinutos += fat.minutos
                    totalValor += fat.valor
                  })

                  // Agrupar apontamentos do faturamento atual por data (se não for cálculo baseado em orçamento)
                  const usarCalculoBaseadoOrcamento = projeto.tipoProjetoCodigo === 3 
                    && etapaSelecionada !== undefined 
                    && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)
                    && projeto.desenvolvimentoOrcado !== undefined
                    && projeto.testesOrcado !== undefined

                  const apontamentosAtuaisPorData = new Map<string, FaturamentoTimeEntry[]>()
                  if (!usarCalculoBaseadoOrcamento) {
                    const timeEntriesSelecionados = projeto.timeEntries.filter(entry => selectedEntries.has(entry.idClockifyTimeEntry))
                    timeEntriesSelecionados.forEach(ap => {
                      const data = ap.dataInicio ? (formatDateToBrazilian(ap.dataInicio) || 'Sem data') : 'Sem data'
                      if (!apontamentosAtuaisPorData.has(data)) {
                        apontamentosAtuaisPorData.set(data, [])
                      }
                      apontamentosAtuaisPorData.get(data)!.push(ap)
                    })
                  }

                  const temDados = faturamentosEnviadosLinhas.length > 0 || baseCustoAtual.minutos > 0 || apontamentosAtuaisPorData.size > 0

                  return (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-theme-sm p-3 w-full min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Base de Custo</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[820px] border-collapse border border-gray-300 text-xs table-fixed">
                          <colgroup>
                            <col className="w-[100px]" />
                            <col className="w-[108px]" />
                            <col className="w-[100px]" />
                            <col className="w-[68px]" />
                            <col className="w-[82px]" />
                            <col className="w-[88px]" />
                            <col className="w-[82px]" />
                            <col className="w-[90px]" />
                            <col className="w-[100px]" />
                          </colgroup>
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="whitespace-nowrap text-left text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Dt. Envio Analista</th>
                              <th className="whitespace-nowrap text-left text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Dt. Envio Momentum</th>
                              <th className="whitespace-nowrap text-left text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Data Faturamento</th>
                              <th className="whitespace-nowrap text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Minutos</th>
                              <th className="whitespace-nowrap text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Horas (Decimal)</th>
                              <th className="whitespace-nowrap text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Horas (Horário)</th>
                              <th className="whitespace-nowrap text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Valor Hora</th>
                              <th className="whitespace-nowrap text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">Valor</th>
                              <th className="whitespace-nowrap text-left text-xs font-medium text-gray-700 py-1.5 px-2 border-b">Observação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!temDados ? (
                              <tr>
                                <td colSpan={9} className="py-4 text-center text-xs text-gray-500">
                                  Nenhum apontamento disponível
                                </td>
                              </tr>
                            ) : (
                              <>
                                {/* Faturamentos enviados - Histórico */}
                                {faturamentosEnviadosLinhas.map((fat, index) => (
                                  <tr key={`enviado-${index}`} className="hover:bg-gray-50">
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">{fat.dataEnvioAnalista}</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">{fat.dataEnvioMomentum}</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">{fat.dataFaturamento}</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">{Math.round(fat.minutos)}</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarHorasDecimal(fat.minutos)}h
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarHorasHorario(fat.minutos)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarMoeda(valorHora)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarMoeda(fat.valor)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b text-xs text-center">
                                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getFaturamentoStatusClasse(fat.statusCodigo)}`}>
                                        {fat.observacao}
                                      </span>
                                      {(() => {
                                        const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === fat.codigoEtapaFaturamento)
                                        return etapa ? (
                                          <p className="text-xs text-gray-500 mt-0.5">{etapa.descricao} ({etapa.percentual}%)</p>
                                        ) : null
                                      })()}
                                    </td>
                                  </tr>
                                ))}
                                
                                {/* Faturamento atual */}
                                {/* Faturamento atual (pré-visualização do envio) */}
                                {/* Sempre UMA linha na Base de Custo, com a soma de todos os apontamentos selecionados */}
                                {baseCustoAtual.minutos > 0 && (
                                  <tr className="hover:bg-gray-50">
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">{dataHoje}</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">-</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">-</td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {Math.round(baseCustoAtual.minutos)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarHorasDecimal(baseCustoAtual.minutos)}h
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarHorasHorario(baseCustoAtual.minutos)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarMoeda(valorHora)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                      {formatarMoeda(baseCustoAtual.valor)}
                                    </td>
                                    <td className="whitespace-nowrap align-middle py-1.5 px-2 border-b text-xs text-center">
                                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getFaturamentoStatusClasse(1)}`}>
                                        Pendente
                                      </span>
                                      {(() => {
                                        const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === etapaSelecionada)
                                        return etapa ? (
                                          <p className="text-xs text-gray-500 mt-0.5">{etapa.descricao} ({etapa.percentual}%)</p>
                                        ) : null
                                      })()}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Total */}
                                <tr className="bg-gray-100 font-semibold">
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900">Total</td>
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900">-</td>
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900">-</td>
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">{Math.round(totalMinutos)}</td>
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                                    {formatarHorasDecimal(totalMinutos)}h
                                  </td>
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                                    {formatarHorasHorario(totalMinutos)}
                                  </td>
                                  <td className="whitespace-nowrap align-middle py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">-</td>
                                  <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                                    {formatarMoeda(totalValor)}
                                  </td>
                                  <td className="py-1.5 px-2 text-xs text-center text-gray-900">-</td>
                                </tr>
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                })()}
              </div>

              {!ocultarApontamentos && projeto.timeEntries.length > 0 && (
                <div className="mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse table-fixed">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b w-32">Responsável</th>
                          <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Descrição</th>
                          <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b w-40">Tags</th>
                          <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b w-28">Data Início</th>
                          <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b w-24">Minutos</th>
                          <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b w-28">Horas</th>
                          <th className="text-center text-sm font-medium text-gray-700 py-2 px-3 border-b w-28">
                            <input
                              type="checkbox"
                              checked={todosSelecionados}
                              onChange={toggleSelecionarTodos}
                              title="Incluir"
                              className="h-4 w-4 text-brand-500 focus:ring-brand-400 border-gray-300 rounded cursor-pointer"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {projeto.timeEntries.map((entry, index) => (
                          <tr key={entry.idClockifyTimeEntry} className={`hover:bg-gray-50 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                            <td className="py-2 px-3 border-b text-sm text-gray-900 whitespace-nowrap">{entry.responsavelNome || '-'}</td>
                            <td className="py-2 px-3 border-b text-sm text-gray-900 break-words">
                              <div className="whitespace-normal break-words">{entry.descricao || '-'}</div>
                            </td>
                            <td className="py-2 px-3 border-b text-sm text-gray-900">
                              {entry.tags && entry.tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {entry.tags.map((tag, tagIndex) => (
                                    <span
                                      key={tagIndex}
                                      className="inline-block px-2 py-1 text-xs font-medium bg-brand-100 text-brand-700 rounded whitespace-nowrap"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="py-2 px-3 border-b text-sm text-gray-900 whitespace-nowrap">
                              {entry.dataInicio ? (formatDateToBrazilian(entry.dataInicio) || '-') : '-'}
                            </td>
                            <td className="py-2 px-3 border-b text-sm text-gray-900 text-right whitespace-nowrap">{entry.minutos || 0}</td>
                            <td className="py-2 px-3 border-b text-sm text-gray-900 text-right whitespace-nowrap">{formatarHoras(entry.minutos || 0)}</td>
                            <td className="py-2 px-3 border-b text-center whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedEntries.has(entry.idClockifyTimeEntry)}
                                onChange={() => toggleEntrySelection(entry.idClockifyTimeEntry)}
                                className="h-4 w-4 text-brand-500 focus:ring-brand-400 border-gray-300 rounded cursor-pointer"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              </div>
            )
          })}

          {(() => {
            // Para projetos tipo orçamento com etapas especiais, calcular resumo por projeto
            // Caso contrário, calcular resumo geral
            if (projetos.length === 1) {
              const projeto = projetos[0]
              const etapaSelecionada = etapaSelecionadaPorProjeto[projeto.id]
              const usarCalculoBaseadoOrcamento = projeto.tipoProjetoCodigo === 3
                && etapaSelecionada !== undefined
                && etapaSelecionada !== null
                && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)
              const timeEntriesSelecionados = usarCalculoBaseadoOrcamento
                ? projeto.timeEntries
                : projeto.timeEntries.filter(entry => selectedEntries.has(entry.idClockifyTimeEntry))
              const resumoGeral = calcularResumoPorUsuario(timeEntriesSelecionados, projeto, etapaSelecionada)
              
              return (
                <div className="mt-6 pt-6 border-t-2 border-gray-300">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumo por Responsável</h4>
                  <div className="flex justify-start">
                    <div className="w-full md:w-1/2">
                      {resumoGeral.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-gray-300">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b border-r border-gray-300">Responsável</th>
                                <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b border-r border-gray-300">Min.</th>
                                <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b">Horas</th>
                              </tr>
                            </thead>
                            <tbody>
                              {resumoGeral.map((resumo, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="py-2 px-3 border-b border-r border-gray-300 text-sm text-gray-900">{resumo.usuario}</td>
                                  <td className="py-2 px-3 border-b border-r border-gray-300 text-sm text-gray-900 text-right">{resumo.minutos}</td>
                                  <td className="py-2 px-3 border-b text-sm text-gray-900 text-right">{resumo.horasFormatada}</td>
                                </tr>
                              ))}
                              <tr className="bg-gray-100 font-semibold">
                                <td className="py-2 px-3 border-r border-gray-300 text-sm text-gray-900">Total</td>
                                <td className="py-2 px-3 border-r border-gray-300 text-sm text-gray-900 text-right">
                                  {resumoGeral.reduce((acc, r) => acc + r.minutos, 0)}
                                </td>
                                <td className="py-2 px-3 text-sm text-gray-900 text-right">
                                  {formatarHoras(resumoGeral.reduce((acc, r) => acc + r.minutos, 0))}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Sem dados para exibir.</div>
                      )}
                    </div>
                  </div>
                </div>
              )
            }
            
            // Para múltiplos projetos, usar cálculo normal (primeiro projeto como referência)
            const todosTimeEntries = projetos.flatMap(p => p.timeEntries)
            const primeiroProjeto = projetos[0]
            const resumoGeral = calcularResumoPorUsuario(todosTimeEntries, primeiroProjeto, undefined)
            
            return (
              <div className="mt-6 pt-6 border-t-2 border-gray-300">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumo por Responsável</h4>
                <div className="flex justify-start">
                  <div className="w-full md:w-1/2">
                    {resumoGeral.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b border-r border-gray-300">Responsável</th>
                              <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b border-r border-gray-300">Min.</th>
                              <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b">Horas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resumoGeral.map((resumo, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="py-2 px-3 border-b border-r border-gray-300 text-sm text-gray-900">{resumo.usuario}</td>
                                <td className="py-2 px-3 border-b border-r border-gray-300 text-sm text-gray-900 text-right">{resumo.minutos}</td>
                                <td className="py-2 px-3 border-b text-sm text-gray-900 text-right">{resumo.horasFormatada}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100 font-semibold">
                              <td className="py-2 px-3 border-r border-gray-300 text-sm text-gray-900">Total</td>
                              <td className="py-2 px-3 border-r border-gray-300 text-sm text-gray-900 text-right">
                                {resumoGeral.reduce((acc, r) => acc + r.minutos, 0)}
                              </td>
                              <td className="py-2 px-3 text-sm text-gray-900 text-right">
                                {formatarHoras(resumoGeral.reduce((acc, r) => acc + r.minutos, 0))}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Sem dados para exibir.</div>
                    )}
                  </div>
                </div>
              </div>
            )
          })()}


          {/* Seção de Documentos - Aprovação e Histórico do Ticket */}
          {projetos.length === 1 && projetos.map((projeto) => {
            return (
            <div key={projeto.id} className="mt-6 pt-6 border-t-2 border-gray-300">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
                    <FileImage className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Documentos</h3>
                    <p className="text-sm text-gray-600">
                      {documentosPorProjeto[projeto.id]?.length || 0} documento(s)
                    </p>
                  </div>
                </div>
                {!showFormDocumento[projeto.id] && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleAbrirFormDocumento(projeto.id, 4)}
                      disabled={!!documentosPorProjeto[projeto.id]?.find(d => d.tipoDocumentoCodigo === 4)}
                      className="px-3 py-1.5 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={documentosPorProjeto[projeto.id]?.find(d => d.tipoDocumentoCodigo === 4) ? 'Documento de Aprovação já existe' : 'Adicionar Aprovação'}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Aprovação</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAbrirFormDocumento(projeto.id, 5)}
                      disabled={!!documentosPorProjeto[projeto.id]?.find(d => d.tipoDocumentoCodigo === 5)}
                      className="px-3 py-1.5 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={documentosPorProjeto[projeto.id]?.find(d => d.tipoDocumentoCodigo === 5) ? 'Documento de Histórico do Ticket já existe' : 'Adicionar Histórico do Ticket'}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Histórico do Ticket</span>
                    </button>
                  </div>
                )}
              </div>

              {showFormDocumento[projeto.id] ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <form onSubmit={handleSubmitDocumento((data) => handleSalvarDocumento({ ...data, projetoId: projeto.id }))} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label={
                          <>
                            Nome do Documento <span className="text-red-500">*</span>
                          </>
                        }
                        error={errorsDocumento.nomeArquivo}
                        labelClassName="block text-sm font-medium text-gray-700 mb-1"
                      >
                        <input
                          type="text"
                          {...registerDocumento('nomeArquivo', { required: 'Nome do documento é obrigatório' })}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                          placeholder="Digite o nome do documento"
                        />
                      </FormField>

                      <FormField
                        label={
                          <>
                            Tipo de Documento <span className="text-red-500">*</span>
                          </>
                        }
                        error={errorsDocumento.tipoDocumentoCodigo}
                        labelClassName="block text-sm font-medium text-gray-700 mb-1"
                      >
                        <select
                          {...registerDocumento('tipoDocumentoCodigo', { required: 'Tipo de documento é obrigatório' })}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                          disabled
                        >
                          <option value="">Selecione o tipo</option>
                          <option value="4">Aprovação</option>
                          <option value="5">Histórico do Ticket</option>
                        </select>
                      </FormField>
                    </div>

                    <div>
                      <FileUpload
                        label={`Arquivo ${!documentoEditando[projeto.id] ? '*' : ''}`}
                        selectedFile={selectedFileDocumento || undefined}
                        onFileSelect={(file) => {
                          setValueDocumento('arquivo', file || null, { shouldValidate: true })
                        }}
                        showPreview={false}
                        error={errorsDocumento.arquivo?.message}
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => handleCancelarDocumento(projeto.id)}
                        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium flex items-center space-x-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>{documentoEditando[projeto.id] ? 'Atualizar' : 'Salvar'}</span>
                      </button>
                    </div>
                  </form>
                </div>
              ) : documentosPorProjeto[projeto.id] && documentosPorProjeto[projeto.id].length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Documento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data de Cadastro
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          AÇÕES
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {documentosPorProjeto[projeto.id].map((doc: any) => (
                        <tr key={doc.idDocumento} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
                                <FileImage className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900">{doc.tipoDocumentoNome || doc.nomeArquivo}</h4>
                                <p className="text-sm text-gray-600 truncate max-w-xs">
                                  {doc.extensao ? `.${doc.extensao}` : ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {formatDateToBrazilian(doc.dataCadastro) || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadDocumento(projeto.id, doc.idDocumento, doc.nomeArquivo)}
                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditarDocumento(projeto.id, doc)}
                                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                  <FileImage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Nenhum documento encontrado</h4>
                  <p className="text-gray-500">Adicione documentos de Aprovação e Histórico do Ticket antes do envio.</p>
                </div>
              )}
            </div>
            )
          })}

          {/* Campo de Observação */}
          {projetos.length === 1 && (() => {
            const projeto = projetos[0]
            const etapaSelecionada = etapaSelecionadaPorProjeto[projeto.id]
            const usarCalculoBaseadoOrcamento = projeto.tipoProjetoCodigo === 3
              && etapaSelecionada !== undefined
              && etapaSelecionada !== null
              && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)
            const timeEntriesSelecionados = projeto.timeEntries.filter(entry => selectedEntries.has(entry.idClockifyTimeEntry))
            const temHorasParaEnviar = usarCalculoBaseadoOrcamento || timeEntriesSelecionados.length > 0
            
            // Para projeto tipo orçamento: verificar se tem faturamento para etapas 3, 4 ou 5
            const faturamentosProjeto = faturamentosEnviadosPorProjeto[projeto.id] || []
            const etapasJaFaturadas = faturamentosProjeto.map(f => f.codigoEtapaFaturamento).filter((e): e is number =>
              e !== undefined && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(e))
            const etapaSelecionadaJaFaturada = etapaSelecionada
              && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada)
              && etapasJaFaturadas.includes(etapaSelecionada)
            
            // Botão deve estar liberado:
            // - Para projetos tipo Apontamento (1): se houver entradas de tempo
            // - Para projetos tipo Orçamento (3): se (tem horas para enviar OU (não tem horas mas etapa selecionada é 3,4,5 e não foi faturada))
            let podeEnviar = false
            if (projeto.tipoProjetoCodigo === 1) {
              // Apontamento: habilitar apenas se houver entradas de tempo
              podeEnviar = temHorasParaEnviar
            } else if (projeto.tipoProjetoCodigo === 3) {
              // Orçamento: lógica atual
              podeEnviar = temHorasParaEnviar || (!temHorasParaEnviar && etapaSelecionada !== undefined && etapaSelecionada !== null && ETAPAS_PROPORCIONAIS_ORCAMENTO.includes(etapaSelecionada) && !etapaSelecionadaJaFaturada)
            } else {
              // Outros tipos: habilitar se houver entradas de tempo
              podeEnviar = temHorasParaEnviar
            }

            return (
              <>
                <div className="mt-6 pt-6 border-t-2 border-gray-300">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observação
                  </label>
                  <textarea
                    value={observacao}
                    onChange={(e) => setObservacao(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Digite observações sobre o faturamento (opcional)"
                  />
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={handleEnviar}
                    disabled={isSubmitting || !podeEnviar}
                    className="px-6 py-2.5 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    <Send className="w-4 h-4 mr-2 inline" />
                    Enviar
                  </button>
                </div>
              </>
            )
          })()}
        </div>
      )}

      <Modal
        isOpen={showModalProjetos}
        onClose={() => {
          setShowModalProjetos(false)
          setProjetosTodos([])
        }}
        title="Selecione o Projeto"
        subtitle="Múltiplos projetos encontrados com esse nome"
        size="4xl"
        icon={
          <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        }
      >
        <div className="space-y-2 p-4">
          {projetosTodos.map((projeto) => (
            <button
              key={projeto.id}
              onClick={() => handleSelecionarProjeto(projeto)}
              className="w-full p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-brand-500 transition-all duration-200 text-left"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 whitespace-nowrap min-w-[100px] text-left">
                    {projeto.numeroTicket ? projeto.numeroTicket : '-'}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-semibold text-gray-900 text-left">{projeto.nome}</p>
                  </div>
                </div>
                <div className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0">
                  Selecionar
                </div>
              </div>
            </button>
          ))}
        </div>
      </Modal>

      {projetos.length === 0 && !isLoading && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Resultados do Faturamento
            </h3>
            <p className="text-gray-600">
              Utilize os filtros acima para buscar projetos
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

